<template>
    <div>
        <div class="user-search mb-6" @keypress.enter="searchUsers">
            <h1>{{ $t("userSearch.title") }}</h1>
            <TDSError v-if="error" :message="error"></TDSError>
            <div class="search-field">
                <div class="fields">
                    <SelectField class="search-key"
                                 :options="searchKeys"
                                 name-key="name"
                                 :init="selectedKey"
                                 val-key="key"
                                 @change="selectedKey = $event"/>
                    <InputField class="input-field"
                                ref="searchQueryInput"
                                :placeholder="$t('userSearch.inputPlaceholder')"
                                :autofocus="true"
                                :validator="validateInput"
                                @enter-pressed="searchUsers"
                                :value.sync="searchQuery">
                        <template #error>
                            {{ inputError }}
                        </template>
                    </InputField>
                    <SelectField class="search-type"
                                 :options="searchTypes"
                                 :init="findExact"
                                 name-key="name"
                                 val-key="key"
                                 ref="searchType"
                                 @change="findExact = $event"/>
                </div>
                <TDSButton button-style="primary-red"
                           width="140px"
                           class="ml-3 submit-button"
                           :show-spinner="isSearching"
                           :inactive="!searchQuery || !selectedKey || isSearching"
                           @click="searchUsers">
                    {{ $t("general.go") }}
                </TDSButton>
            </div>
        </div>
        <div class="results fade-in mb-6" v-if="results">
            <TableList :columns="searchResultColumns"
                       :rows="results"
                       :is-clickable="true"
                       @row-clicked="showUserDetailPage">
                <template slot="column_status" slot-scope="{data}">
                    <span :class="data.activated ? 'active' : 'inactive'" v-tooltip="data.activated ? 'Active' : 'Inactive'"></span>
                </template>
                <template slot="column_id" slot-scope="{data}">
                    {{ data.id.substring(0, 12) + (data.id.length > 12 ? "..." : "") }}
                </template>
                <template slot="column_email" slot-scope="{data}">
                    {{ data.email }}
                </template>
                <template slot="column_text" slot-scope="{data}">
                    <div style="display:flex; justify-content: flex-end">
                        <span v-if="data.restrictions.length" class="restricted" v-tooltip="'user is restricted'" />
                        <span v-if="data.iccid && data.iccid.length > 0" class="has-esim" v-tooltip="'Has data plans'" />
                    </div>
                </template>
                <template slot="column_firstName" slot-scope="{data}">
                    {{ data.firstName }}
                </template>
                <template slot="column_lastName" slot-scope="{data}">
                    {{ data.lastName }}
                </template>
            </TableList>
        </div>
        <UserFlagsList></UserFlagsList>
    </div>
</template>

<script lang="ts">
import {Component, Vue, Watch} from "vue-property-decorator";
import InputField from "@/components/common/InputField.vue";
import TDSButton from "@/components/common/TDSButton.vue";
import SelectField from "@/components/common/SelectField.vue";
import {SearchKeyItem} from "@/interfaces/entities/SearchKeyItem";
import VueI18n from "vue-i18n";
import TranslateResult = VueI18n.TranslateResult;
import TDSError from "@/components/common/TDSError.vue";
import {GetUsersResponse} from "@/interfaces/httpResponses/GetUsersResponse";
import {DentUser} from "@/interfaces/entities/DentUser";
import TableList, {TableListColumnConfig} from "@/components/common/TableList.vue";
import regexes from "@/util/regexes";
import {KeyNameDictionaryItem} from "@/interfaces/entities/KeyNameDictionaryItem";
import UserFlagsList from "@/components/partials/UserFlags/UserFlagsList.vue";


@Component({
    components: {SelectField, TDSButton, InputField, TDSError, TableList, UserFlagsList}
})
export default class UsersPage extends Vue {

    private findExact: string = decodeURIComponent((this.$route.query.exact ?? "") as string); // "true" or "false" :D
    private searchQuery: string = decodeURIComponent((this.$route.query.query ?? "") as string);
    private selectedKey: string = decodeURIComponent((this.$route.query.key ?? "") as string);

    private isSearching: boolean = false;
    private searchKeys: Array<SearchKeyItem> = [];
    private searchTypes: Array<KeyNameDictionaryItem<string>> = [
        {
            name: "Find Exact",
            key: "true"
        },
        {
            name: "Find All",
            key: "false"
        }
    ];
    private error: string | TranslateResult = "";
    private inputError: string = "";
    private results: Array<DentUser> | null = null;

    get isMobile(): boolean {
        return this.$store.state.isMobile;
    }

    get searchResultColumns(): TableListColumnConfig {
        if (this.isMobile) {
            return {
                id: {label: "#", width: "120px"},
                email: {label: this.$t("general.email") + ""}
            };
        }
        return {
            status: {label: "", width:"35px"},
            id: {label: "#", width: "120px"},
            email: {label: this.$t("general.email") + ""},
            text: {label:"", width: "55px"},
            firstName: {label: this.$t("general.firstName") + ""},
            lastName: {label: this.$t("general.lastName") + ""}
        };
    }

    get searchQueryRequestParams(): string {
        return `page_size=100&page_index=1&search_key=${this.selectedKey}&search_query=${encodeURIComponent(this.searchQuery)}&find_exact=${this.findExact}`;
    }

    @Watch("selectedKey")
    selectedKeyChanged() {
        this.updateUrlQuery({
            key: encodeURIComponent(this.selectedKey) || undefined
        });
    }

    @Watch("findExact")
    findExactChanged() {
        this.updateUrlQuery({
            exact: encodeURIComponent(this.findExact) || undefined
        });
    }

    @Watch("searchQuery")
    searchQueryChanged() {
        this.error = "";
        if (this.selectedKey === "email" || this.selectedKey === "msisdn") {
            this.$nextTick(() => this.searchQuery = this.searchQuery.replace(/\s/g, ""));
        }
        this.updateUrlQuery({
            query: encodeURIComponent(this.searchQuery) || undefined
        });
    }

    created() {
        this.init();
    }

    mounted() {
        if (this.$route.query.exact) {
            (this.$refs.searchType as SelectField<string>)
                .setOption(this.searchTypes
                    .find((type: KeyNameDictionaryItem<string>) => type.key === this.$route.query.exact));
        }
    }

    async init() {
        try {

            //  In case the URL query contains the last search query input
            //  we can grab the last search results for that query from the
            //  local storage. In the 'searchUsers' method, those search results
            //  are stored in the storage
            if(this.searchQuery) {
                const cachedResults = window.localStorage.getItem("last-user-search-results");
                if (cachedResults) {
                    this.results = JSON.parse(cachedResults);
                }
            }

            const {data} = await this.$store.dispatch("FETCH_SEARCH_KEYS");
            this.searchKeys = data;
        } catch (e) {
            this.error = this.$t("userSearch.error.loading", [e.message]);
        }
    }

    private updateUrlQuery(q?: { [key: string]: string | undefined }) {
        const query = {
            ...this.$route.query,
            ...q
        };
        this.$router.replace({query});
    }

    private validateInput(): boolean {
        let isValid = true;
        this.inputError = "";

        // If the input type is email or phone number and we are looking for
        // an exact match, we check if the input value is valid
        // else we can search for any value entered
        if (this.findExact === "true" && this.selectedKey === "email") {
            isValid = regexes.emailRegex.test(this.searchQuery);
            if (!isValid) this.inputError = this.$t("general.error.invalidEmail") + "";
        }
        if (this.findExact === "true" && this.selectedKey === "msisdn") {
            isValid = regexes.phoneNumberRegex.test(this.searchQuery);
            if (!isValid) this.inputError = this.$t("general.error.invalidPhoneNumber") + "";
        }
        return isValid;
    }

    private async searchUsers() {
        try {

            //  This is going to trigger the validation and returns the value of
            //  inputIsValid as a boolean
            if (!(this.$refs.searchQueryInput as InputField).submit()) return;

            this.results = null;
            this.error = "";
            this.isSearching = true;
            const {data} = (await this.$store
                .dispatch("FETCH_USERS", this.searchQueryRequestParams)) as GetUsersResponse;
            this.results = data.users;
            try {
                window.localStorage.setItem("last-user-search-results", JSON.stringify(this.results));
            } catch (e) {
                console.error("[UserSearch] Couldn't cache search results in local storage: ", e);
            }
        } catch (e) {
            this.error = this.$t("userSearch.error.loading", [e.message]);
        } finally {
            this.isSearching = false;
        }
    }

    showUserDetailPage(userId: string) {
        this.$router.push("/user/" + userId + "/general");
    }
}
</script>

<style lang="scss" scoped>
.user-search {
    @include card();

    .search-field {
        display: flex;

        @media only screen and (max-width: $breakPoint - 1px) {
            flex-wrap: wrap;
        }

        .fields {
            flex: 1;
            display: flex;

            @media only screen and (max-width: $breakPoint - 1px) {
                flex-wrap: wrap;
                ::v-deep .input-group {
                    width: 100%;
                    margin-bottom: 16px;

                    input {
                        border-radius: 3px;
                    }
                }
            }

            .search-key {
                ::v-deep input {
                    @media only screen and (max-width: $breakPoint - 1px) {
                        border-radius: 3px;
                    }
                    @media only screen and (min-width: $breakPoint) {
                        border-radius: 3px 0 0 3px;
                        border-right: none;
                    }
                }
            }

            .input-field {

                @media only screen and (max-width: $breakPoint - 1px) {
                    flex: initial;

                    ::v-deep input {
                        border-radius: 3px;
                    }
                }

                @media only screen and (min-width: $breakPoint) {
                    flex: 1;

                    ::v-deep input {
                        border-radius: 0;
                    }
                }
            }
        }

        .submit-button {
            @media only screen and (max-width: $breakPoint - 1px) {
                margin: 0;
            }
        }
    }
}

.results {
    @include card();

    ::v-deep .clickable-row {
        background-image: url("../../assets/icons/globe-nav-chevron-right-grey56.svg");
        background-position: right 8px top 10px;
        background-repeat: no-repeat;
        background-size: 20px;
    }
}



span.inactive {
    background: transparent url('../../assets/icons/account-dent-red.svg') no-repeat center;
    background-size: 18px auto;
    height: 18px;
    width: 18px;
    display: block;
}
span.active {
    background: transparent url('../../assets/icons/account-green.svg') no-repeat center;
    height: 18px;
    width: 18px;
    background-size: 18px;
    display: block;
}


.restricted {
    background: transparent url("../../assets/icons/close-circle-dent-red.svg") no-repeat left center;
    height: 18px;
    width: 18px;
    background-size: 18px;
    display: block;
}

.has-esim {
    background: transparent url("../../assets/icons/esim-green.svg") no-repeat left center;
    height: 18px;
    width: 18px;
    background-size: 18px;
    display: block;
}
</style>
